/**
 * LinkedFieldBanner — Iter217
 * Non-blocking conflict banner. Rendered inline inside a modal or form.
 *
 * Props:
 *   conflicts: [{field, current, expected}]
 *   labels:    { field_key: "Human label" }
 *   resolve:   { current: (field, value)=>string, expected: (field, value)=>string }
 *   onApplyExpected: (field) => void   — accept the linked source's value
 *   onKeepCurrent:   (field) => void   — dismiss the warning for this field
 */
import React from "react";
import { AlertTriangle } from "lucide-react";

export default function LinkedFieldBanner({
  conflicts,
  labels = {},
  resolve = {},
  onApplyExpected,
  onKeepCurrent,
}) {
  if (!conflicts || conflicts.length === 0) return null;

  const showVal = (side, c) => {
    const fn = resolve[side];
    if (typeof fn === "function") return fn(c.field, c[side]) || "—";
    return c[side] || "—";
  };

  return (
    <div
      className="my-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
      data-testid="linked-field-conflict-banner"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
        <div className="flex-1 space-y-1.5">
          <div className="font-semibold">
            Bağlantılı kayıtla eşleşmeyen alan{conflicts.length > 1 ? "lar" : ""} var
          </div>
          {conflicts.map((c) => (
            <div
              key={c.field}
              className="flex flex-wrap items-center gap-2"
              data-testid={`linked-conflict-${c.field}`}
            >
              <span className="font-medium">{labels[c.field] || c.field}:</span>
              <span className="rounded bg-white px-1.5 py-0.5 border border-slate-200">
                Elle: <b>{showVal("current", c)}</b>
              </span>
              <span>vs.</span>
              <span className="rounded bg-white px-1.5 py-0.5 border border-slate-200">
                Projeden: <b>{showVal("expected", c)}</b>
              </span>
              <button
                type="button"
                className="rounded bg-amber-600 text-white px-2 py-0.5 hover:bg-amber-700"
                onClick={() => onApplyExpected?.(c.field)}
                data-testid={`linked-apply-expected-${c.field}`}
              >
                Projeden gelen değeri kullan
              </button>
              <button
                type="button"
                className="rounded border border-amber-400 text-amber-900 px-2 py-0.5 hover:bg-amber-100"
                onClick={() => onKeepCurrent?.(c.field)}
                data-testid={`linked-keep-current-${c.field}`}
              >
                Elle girdiğim değeri koru
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
